
import React, { useState, useEffect } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Input, Textarea } from './ui/Input';
import { ExpenseEntry, CostCategoryDetail, MainCostCategory } from '../types';
import { formatMonthForInput } from '../constants';

interface AddDirectExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<ExpenseEntry, 'id' | 'projectId'>) => void;
  categoryDetails: CostCategoryDetail;
  existingExpense?: ExpenseEntry | null;
}

export const AddDirectExpenseModal: React.FC<AddDirectExpenseModalProps> = ({ isOpen, onClose, onSubmit, categoryDetails, existingExpense }) => {
  const [formData, setFormData] = useState({
    month: '',
    amount: '',
    description: '',
    observation: '',
  });

  useEffect(() => {
    if (existingExpense) {
      setFormData({
        month: formatMonthForInput(existingExpense.month),
        amount: String(existingExpense.amount),
        description: existingExpense.description || '',
        observation: existingExpense.observation || '',
      });
    } else {
      // Reset form for new entry
      setFormData({
        month: formatMonthForInput(undefined),
        amount: '',
        description: '',
        observation: '',
      });
    }
  }, [existingExpense, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.month || !formData.amount || !formData.description) {
      alert('Mês, Valor e Descrição são obrigatórios.');
      return;
    }

    const subCategoryInfo = categoryDetails.subItems[0] || {id: 'direct', name: 'Direct Entry'};

    onSubmit({
      mainCategory: categoryDetails.id as MainCostCategory,
      subCategoryItemId: subCategoryInfo.id,
      subCategoryItemName: subCategoryInfo.name,
      amount: parseFloat(formData.amount),
      date: new Date().toISOString(), // Date is less relevant for direct entries, but required by type
      month: formData.month,
      description: formData.description,
      observation: formData.observation,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`${existingExpense ? 'Editar' : 'Adicionar'} Lançamento em ${categoryDetails.name}`} size="xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Mês de Referência*"
              name="month"
              type="month"
              value={formData.month}
              onChange={handleChange}
              required
            />
            <Input
              label="Valor (R$)*"
              name="amount"
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={handleChange}
              required
            />
        </div>
        <Input
          label="Descrição*"
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
        />
        <Textarea
          label="Observação"
          name="observation"
          value={formData.observation}
          onChange={handleChange}
          rows={3}
        />
        <div className="flex justify-end space-x-3 pt-4 border-t border-dark-tertiary mt-6">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary">
            {existingExpense ? 'Salvar Alterações' : 'Adicionar Lançamento'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
